import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import {
  EditorialCard, Empty, FaqList, Grid, Loading, PageHero, Pill, Section, SectionHead,
} from "../components/shared";
import { getList, destUrl, inr } from "../lib/api";

const COPY = {
  international: {
    eyebrow: "International Holidays",
    title: "Custom international itineraries from India",
    subtitle:
      "Private journeys shaped around your interests, preferred stays and departure city in India. Explore our destinations, then refine the route with a travel designer.",
    hero: null,
  },
  india: {
    eyebrow: "India Holidays",
    title: "Private luxury holidays in India",
    subtitle:
      "Palace stays, Himalayan landscapes and quiet backwaters. Design a private India holiday with Azulo Travel, tailored to your dates, interests and preferred pace.",
    hero: null,
  },
};

export default function DestinationList({ region }) {
  const params = useParams();
  const reg = region || params.region || "international";
  const [items, setItems] = useState(null);
  const [style, setStyle] = useState("");
  const [styles, setStyles] = useState([]);

  useEffect(() => {
    setItems(null);
    getList("destinations", { region: reg, limit: 100 })
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
    getList("travel_styles", { limit: 30 }).then((d) => setStyles(d.items || [])).catch(() => {});
  }, [reg]);

  const filtered = useMemo(
    () => (items || []).filter((d) => !style || (d.styles || []).includes(style)),
    [items, style]
  );

  const copy = COPY[reg] || COPY.international;
  if (!items) return <Loading label="Loading destinations" />;

  const hero = items[0]?.hero_image;

  return (
    <>
      <Seo
        title={reg === "india" ? "Private Luxury Holidays in India | Azulo Travel" : "Custom International Itineraries from India | Azulo Travel"}
        description={copy.subtitle}
        image={hero}
        path={`/${reg}`}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: copy.eyebrow, to: `/${reg}` }]}
      />
      <PageHero image={hero} eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} height="short" />

      <Section testId="destination-list">
        <div
          data-testid="destination-style-filter"
          className="mb-10 flex gap-2 overflow-x-auto pb-2"
        >
          <Pill active={!style} onClick={() => setStyle("")} testId="filter-all">
            All styles
          </Pill>
          {styles.map((s) => (
            <Pill
              key={s.slug}
              active={style === s.slug}
              onClick={() => setStyle(s.slug)}
              testId={`filter-${s.slug}`}
            >
              {s.title}
            </Pill>
          ))}
        </div>

        {filtered.length === 0 ? (
          <Empty
            title="No destinations match that style yet"
            body="Try another travel style, or tell us what you have in mind and we will design it."
          />
        ) : (
          <Grid cols={3}>
            {filtered.map((d) => (
              <EditorialCard
                key={d.slug}
                to={destUrl(d)}
                image={d.card_image || d.hero_image}
                title={d.title}
                meta={`${d.country} · ${d.duration_note} · from ${inr(d.starting_price_inr)}`}
                subtitle={d.tagline}
                testId={`destination-card-${d.slug}`}
              />
            ))}
          </Grid>
        )}
      </Section>

      <Section wash testId="destination-list-cta">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <SectionHead
              eyebrow="Not listed?"
              title="We travel further than this page"
              subtitle="These are the destinations we publish. We regularly design journeys to a great deal more — tell us where you have in mind."
            />
            <FaqList
              faqs={[
                {
                  question: "Can you design a destination that is not listed?",
                  answer:
                    "Yes. We publish the destinations we know best, but we design journeys well beyond this list. If we do not have first-hand knowledge, we will tell you plainly and partner with a specialist rather than improvise.",
                },
                {
                  question: "How far ahead should we plan?",
                  answer:
                    "Six to nine months for peak-season Japan, Africa and Europe. Three to four months for most of Asia and India. Less is possible, with fewer choices.",
                },
              ]}
              testId="destination-list-faq"
            />
          </div>
          <InquiryForm
            source="destination"
            title="Tell us where you would like to go"
            subtitle="We reply within one business day with a real itinerary."
          />
        </div>
      </Section>
    </>
  );
}
